import React, { useState, useMemo, useEffect } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useRawTabVisible } from '../../hooks/useRawTabVisible';
import { safeStorage } from '../../utils/safeStorage';
import './ProfileModal.css';

interface User {
  id?: string;
  name?: string;
  email?: string;
  roles?: string[];
  [key: string]: any;
}

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

interface DecodedToken {
  header: Record<string, any> | null;
  payload: Record<string, any> | null;
  signature: string | null;
  raw: string | null;
  error?: string;
}

const decodeJWT = (token: string | null): DecodedToken => {
  if (!token) {
    return { header: null, payload: null, signature: null, raw: null, error: 'No token found' };
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return { header: null, payload: null, signature: null, raw: token, error: 'Invalid JWT format' };
    }

    const header = JSON.parse(atob(parts[0]));
    const payload = JSON.parse(atob(parts[1]));
    const signature = parts[2];

    return { header, payload, signature, raw: token };
  } catch (e) {
    return { header: null, payload: null, signature: null, raw: token, error: 'Failed to decode token' };
  }
};


const isExpired = (exp: number): boolean => {
  if (!exp) return false;
  return Date.now() > exp * 1000;
};

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, user }) => {
  const { formatDateTime, t } = useI18n();
  const [activeTab, setActiveTab] = useState<'overview' | 'claims' | 'raw'>('overview');
  const [copied, setCopied] = useState(false);
  const rawTabVisible = useRawTabVisible();

  // If admin hid RAW while user was on it, snap back to Overview.
  useEffect(() => {
    if (rawTabVisible === false && activeTab === 'raw') {
      setActiveTab('overview');
    }
  }, [rawTabVisible, activeTab]);

  const accessToken = safeStorage.getItem('access_token');
  const decodedToken = useMemo(() => decodeJWT(accessToken), [accessToken]);

  const formatTimestamp = (timestamp: number): string =>
    timestamp ? formatDateTime(timestamp * 1000) : 'N/A';

  if (!isOpen) return null;

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const handleCopyToken = async () => {
    if (decodedToken.raw) {
      await navigator.clipboard.writeText(decodedToken.raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const exportData = {
      exportedAt: new Date().toISOString(),
      user: user,
      token: {
        raw: decodedToken.raw,
        header: decodedToken.header,
        payload: decodedToken.payload,
      },
      localStorage: {
        access_token: accessToken,
        token_type: safeStorage.getItem('token_type'),
        expires_at: safeStorage.getItem('expires_at'),
        refresh_token: safeStorage.getItem('refresh_token') ? '[PRESENT]' : null,
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `token-debug-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const tokenPayload = decodedToken.payload;
  const tokenExpired = tokenPayload?.exp ? isExpired(tokenPayload.exp) : false;

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal-content profile-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="profile-modal-header">
          <h2>Profile & Token Information</h2>
          <button className="profile-modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`profile-tab ${activeTab === 'claims' ? 'active' : ''}`}
            onClick={() => setActiveTab('claims')}
          >
            Token Claims
          </button>
          {rawTabVisible !== false && (
            <button
              className={`profile-tab ${activeTab === 'raw' ? 'active' : ''}`}
              onClick={() => setActiveTab('raw')}
            >
              Raw Token
            </button>
          )}
        </div>

        <div className="profile-modal-body">
          {activeTab === 'overview' && (
            <div className="profile-details">
              {user ? (
                <>
                  <div className="profile-section">
                    <h3>{t('profile.userInformation')}</h3>
                    <div className="profile-grid">
                      <div className="profile-item">
                        <label>User ID:</label>
                        <span>{formatValue(user.id || tokenPayload?.sub)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Username:</label>
                        <span>{formatValue(user.name || tokenPayload?.preferred_username)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Email:</label>
                        <span>{formatValue(user.email || tokenPayload?.email)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Email Verified:</label>
                        <span>{formatValue(tokenPayload?.email_verified)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Full Name:</label>
                        <span>{formatValue(tokenPayload?.name)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Given Name:</label>
                        <span>{formatValue(tokenPayload?.given_name)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Family Name:</label>
                        <span>{formatValue(tokenPayload?.family_name)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="profile-section">
                    <h3>{t('profile.tokenStatus')}</h3>
                    <div className="profile-grid">
                      <div className="profile-item">
                        <label>Token Status:</label>
                        <span className={tokenExpired ? 'token-expired' : 'token-valid'}>
                          {tokenExpired ? 'EXPIRED' : 'VALID'}
                        </span>
                      </div>
                      <div className="profile-item">
                        <label>Issued At:</label>
                        <span>{formatTimestamp(tokenPayload?.iat)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Expires At:</label>
                        <span>{formatTimestamp(tokenPayload?.exp)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Not Before:</label>
                        <span>{formatTimestamp(tokenPayload?.nbf)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Auth Time:</label>
                        <span>{formatTimestamp(tokenPayload?.auth_time)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Session ID:</label>
                        <span>{formatValue(tokenPayload?.sid)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="profile-section">
                    <h3>Authorization</h3>
                    <div className="profile-grid">
                      <div className="profile-item">
                        <label>Issuer:</label>
                        <span>{formatValue(tokenPayload?.iss)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Audience:</label>
                        <span>{formatValue(tokenPayload?.aud)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Authorized Party:</label>
                        <span>{formatValue(tokenPayload?.azp)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Scope:</label>
                        <span>{formatValue(tokenPayload?.scope)}</span>
                      </div>
                      <div className="profile-item">
                        <label>Token Type:</label>
                        <span>{formatValue(tokenPayload?.typ)}</span>
                      </div>
                      <div className="profile-item">
                        <label>ACR:</label>
                        <span>{formatValue(tokenPayload?.acr)}</span>
                      </div>
                    </div>
                  </div>

                  {tokenPayload?.realm_access?.roles && (
                    <div className="profile-section">
                      <h3>{t('profile.realmRoles')}</h3>
                      <div className="profile-roles">
                        {tokenPayload.realm_access.roles.map((role: string, index: number) => (
                          <span key={index} className="profile-role-badge">{role}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {tokenPayload?.resource_access && (
                    <div className="profile-section">
                      <h3>{t('profile.resourceAccess')}</h3>
                      <div className="profile-raw-data">
                        <pre>{JSON.stringify(tokenPayload.resource_access, null, 2)}</pre>
                      </div>
                    </div>
                  )}

                  <div className="profile-section">
                    <h3>Groups (Client Access)</h3>
                    {tokenPayload?.groups && tokenPayload.groups.length > 0 ? (
                      <div className="profile-groups">
                        {tokenPayload.groups.map((group: string, index: number) => {
                          const isClientGroup = group.startsWith('/Clients/');
                          return (
                            <div key={index} className={`profile-group-item ${isClientGroup ? 'client-group' : ''}`}>
                              <span className="group-path">{group}</span>
                              {isClientGroup && (
                                <span className="group-type-badge">{t('profile.clientAccess')}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="profile-no-groups">
                        <p>{t('profile.noGroupsAssigned')}</p>
                        <p className="profile-hint">
                          Users need group membership in /Clients/{'<client-id>'} to access client-specific data.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="profile-no-data">
                  <p>{t('profile.noUserInformation')}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'claims' && (
            <div className="profile-details">
              <div className="profile-section">
                <h3>{t('profile.tokenHeader')}</h3>
                <div className="profile-raw-data">
                  <pre>{decodedToken.header ? JSON.stringify(decodedToken.header, null, 2) : 'Unable to decode header'}</pre>
                </div>
              </div>

              <div className="profile-section">
                <h3>Token Payload (All Claims)</h3>
                <div className="profile-raw-data">
                  <pre>{decodedToken.payload ? JSON.stringify(decodedToken.payload, null, 2) : 'Unable to decode payload'}</pre>
                </div>
              </div>

              <div className="profile-section">
                <h3>User Object (from AuthContext)</h3>
                <div className="profile-raw-data">
                  <pre>{user ? JSON.stringify(user, null, 2) : 'No user object available'}</pre>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'raw' && rawTabVisible !== false && (
            <div className="profile-details">
              <div className="profile-section">
                <div className="profile-section-header">
                  <h3>Raw Access Token (JWT)</h3>
                  <button
                    className="profile-copy-btn"
                    onClick={handleCopyToken}
                    disabled={!decodedToken.raw}
                  >
                    {copied ? 'Copied!' : 'Copy Token'}
                  </button>
                </div>
                <div className="profile-raw-data profile-token-raw">
                  <pre>{decodedToken.raw || 'No token available'}</pre>
                </div>
              </div>

              <div className="profile-section">
                <h3>{t('profile.tokenParts')}</h3>
                {decodedToken.raw && (
                  <div className="token-parts">
                    <div className="token-part">
                      <label>Header (Base64):</label>
                      <code>{decodedToken.raw.split('.')[0]}</code>
                    </div>
                    <div className="token-part">
                      <label>Payload (Base64):</label>
                      <code>{decodedToken.raw.split('.')[1]}</code>
                    </div>
                    <div className="token-part">
                      <label>Signature:</label>
                      <code>{decodedToken.signature}</code>
                    </div>
                  </div>
                )}
              </div>

              <div className="profile-section">
                <h3>{t('profile.localStorage')}</h3>
                <div className="profile-grid">
                  <div className="profile-item">
                    <label>Token Type:</label>
                    <span>{safeStorage.getItem('token_type') || 'N/A'}</span>
                  </div>
                  <div className="profile-item">
                    <label>Expires At (localStorage):</label>
                    <span>
                      {safeStorage.getItem('expires_at')
                        ? formatDateTime(parseInt(safeStorage.getItem('expires_at')!))
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="profile-item">
                    <label>Refresh Token:</label>
                    <span>{safeStorage.getItem('refresh_token') ? 'Present' : 'Not found'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="profile-modal-footer">
          <button className="profile-modal-button secondary" onClick={handleDownload}>
            Download Debug Info
          </button>
          <button className="profile-modal-button" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
};
